/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {FC, CanBeSignals, ContainerProps, $Container, getProp, iterChildren} from '@gongxigames/framework/core/fragments/BuilderY';
import {utilArray} from '@gongxigames/framework/lib/util/utilArray';
import {Timer, Store} from '@gongxigames/framework/core';
import {IHitArea, Point, TextStyle} from 'pixi.js';
import {LSViewConfig} from '@gongxigames/framework/slotV2/modules/loadingScreenV2/loadingScreenViewConfig';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {SlidesComponents} from '../../types';

type SlidesProps = ContainerProps & CanBeSignals<{
    dotsPosition: Point;
    slidesPosition: Point;
    textPosition: Point;
    autoSlideTime?: number;
    defaultSlideTime: number;
    dotsHide: boolean;
    arrowsHide: boolean;
    arrowsPosition: Point;
    hitAreaDots: IHitArea | null;
    hitAreaArrows: IHitArea | null;
    reactiveSlidesScale: number;
    slidesComponents: SlidesComponents;
}>;

export const $Slides: FC<SlidesProps> = (props) => {

    const {
        $SlidesPresentation,
        $Arrows,
        $ArrowButton,
        $DotButtons,
        $TextSlides,
        $DotButton
    } = getProp(props.slidesComponents) as SlidesComponents;

    const activeIndex = Store.signal(0);

    const style = new TextStyle(LSViewConfig.textFeatureDescription.defaultStyle);

    const children = [...iterChildren(props.children)];

    function setIndex(nextIndex: number) {
        activeIndex.value = utilArray.normalizeIndexFromReelStrip(children.length, nextIndex);
    }

    let timer: Timer;

    function startAutoSlide() {
        const time = props.autoSlideTime ?? props.defaultSlideTime;

        timer?.stop();

        timer = slotStore.updateManager.value!.addTimer(() => {
            activeIndex.value = utilArray.normalizeIndexFromReelStrip(children.length, activeIndex.value + 1);

            startAutoSlide();
        }, time as number, true);
    }
    if (props.dotsHide) {
        return <></>;
    }

    startAutoSlide();

    const text = Store.computed(() => {
        const key = LSViewConfig.textFeatureDescription.textFeatureTranslationsList[activeIndex.value];

        return key && slotStore.languageService.value?.get(key) || '';
    });

    return (
        <$Container name='Slides' {...props} scale={props.reactiveSlidesScale}>
            <$SlidesPresentation activeIndex={activeIndex} slides={children} slidesPosition={props.slidesPosition} />

            <$Arrows
                arrowButton={$ArrowButton}
                hitArea={props.hitAreaArrows}
                invisible={props.arrowsHide}
                activeIndex={activeIndex}
                setIndex={setIndex}
                arrowsPosition={props.arrowsPosition}
            />

            <$TextSlides
                textPosition={props.textPosition}
                activeIndex={activeIndex}
                text={text}
                style={style} />

            <$DotButtons
                dotButton={$DotButton}
                hitArea={props.hitAreaDots}
                invisible={props.dotsHide}
                slides={children}
                activeIndex={activeIndex}
                setIndex={setIndex}
                dotsPosition={props.dotsPosition}
                dotOffset={LSViewConfig.dotButtonParams.dotOffset}
            />
        </$Container>
    );
};
